import jsPDF from 'jspdf';

/**
 * Generate a PDF receipt for an appointment
 * @param {Object} appointment - The appointment object with all details
 * @param {Object} clinicInfo - Clinic branding and contact information
 */
export const generateAppointmentReceipt = (appointment, clinicInfo = {}) => {
  const doc = new jsPDF();

  // Default clinic info
  const clinic = {
    name: clinicInfo?.name || 'Shree Dhanwantari Ayurveda Panchkarma',
    address: clinicInfo?.address || 'Pune, Maharashtra, India',
    phone: clinicInfo?.phone || '+91 XXXXXXXXXX',
    email: clinicInfo?.email || 'info@sdapk.com',
    website: clinicInfo?.website || 'www.sdapk.com',
    gst: clinicInfo?.gst || 'GST: XXXXXXXXXXXX'
  };

  // Colors
  const primaryColor = [41, 128, 185]; // Blue
  const secondaryColor = [52, 73, 94]; // Dark gray
  const accentColor = [46, 204, 113]; // Green

  let yPos = 20;

  // Header - Clinic Name
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, 210, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(clinic?.name, 105, 20, { align: 'center' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(clinic?.address, 105, 28, { align: 'center' });
  doc.text(`${clinic?.phone} | ${clinic?.email}`, 105, 34, { align: 'center' });

  yPos = 50;

  // Receipt Title
  doc.setTextColor(...secondaryColor);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('APPOINTMENT RECEIPT', 105, yPos, { align: 'center' });

  yPos += 15;

  // Receipt Details Box
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(20, yPos, 190, yPos);

  yPos += 8;

  // Appointment ID and Date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...secondaryColor);
  doc.text(`Receipt ID: ${appointment?.id?.substring(0, 8)?.toUpperCase()}`, 20, yPos);
  doc.text(`Date: ${new Date()?.toLocaleDateString('en-IN')}`, 190, yPos, { align: 'right' });

  yPos += 12;

  // Client Information Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Client Information', 20, yPos);
  yPos += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Name: ${appointment?.client?.full_name || appointment?.patient?.full_name || 'N/A'}`, 20, yPos);
  yPos += 6;
  doc.text(`Email: ${appointment?.client?.email || appointment?.patient?.email || 'N/A'}`, 20, yPos);
  yPos += 6;
  doc.text(`Phone: ${appointment?.client?.phone || appointment?.patient?.phone || 'N/A'}`, 20, yPos);

  yPos += 12;

  // Appointment Details Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Appointment Details', 20, yPos);
  yPos += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  // Booking Type
  const bookingTypeLabel = appointment?.booking_type === 'consultation' ? 'Consultation' :
    appointment?.booking_type === 'service' ? 'Wellness Service' : 'Treatment';
  doc.text(`Type: ${bookingTypeLabel}`, 20, yPos);
  yPos += 6;

  // Doctor/Service/Treatment
  if (appointment?.doctor?.full_name) {
    doc.text(`Doctor: ${appointment?.doctor?.full_name}`, 20, yPos);
    yPos += 6;
  }

  if (appointment?.treatment?.title) {
    doc.text(`Treatment: ${appointment?.treatment?.title}`, 20, yPos);
    yPos += 6;
  }

  doc.text(`Date: ${appointment?.preferred_date}`, 20, yPos);
  yPos += 6;
  doc.text(`Time: ${appointment?.preferred_time}`, 20, yPos);
  yPos += 6;
  doc.text(`Mode: ${appointment?.consultation_mode?.replace('_', ' ')?.toUpperCase() || 'IN PERSON'}`, 20, yPos);
  yPos += 6;
  doc.text(`Status: ${appointment?.status?.replace('_', ' ')?.toUpperCase()}`, 20, yPos);

  yPos += 12;

  // Payment Details Section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Details', 20, yPos);
  yPos += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Payment Method: ${appointment?.payment_method?.toUpperCase() || 'N/A'}`, 20, yPos);
  yPos += 6;
  doc.text(`Payment Status: ${appointment?.payment_status?.toUpperCase() || 'PENDING'}`, 20, yPos);

  yPos += 12;

  // Fee Breakdown
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(1);
  doc.line(20, yPos, 190, yPos);
  yPos += 10;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...accentColor);
  doc.text('Total Amount:', 20, yPos);
  doc.text(`₹${appointment?.fee || 0}`, 190, yPos, { align: 'right' });

  yPos += 8;
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(1);
  doc.line(20, yPos, 190, yPos);

  yPos += 15;

  // Footer - Terms and Conditions
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 100);
  doc.text('Terms & Conditions:', 20, yPos);
  yPos += 4;
  doc.text('• This receipt is valid for the appointment mentioned above.', 20, yPos);
  yPos += 4;
  doc.text('• Please arrive 10 minutes before your scheduled appointment time.', 20, yPos);
  yPos += 4;
  doc.text('• Cancellations must be made 24 hours in advance for a full refund.', 20, yPos);
  yPos += 4;
  doc.text('• For any queries, please contact us at the above contact details.', 20, yPos);

  yPos += 10;

  // Footer - Thank You Message
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('Thank you for choosing our services!', 105, yPos, { align: 'center' });

  yPos += 6;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(clinic?.website, 105, yPos, { align: 'center' });

  // Add page border
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.rect(10, 10, 190, 277);

  // Generate filename
  const fileName = `Receipt_${appointment?.client?.full_name || appointment?.patient?.full_name || 'Appointment'}_${appointment?.id?.substring(0, 8)}.pdf`;

  // Save the PDF
  doc.save(fileName);
};

/**
 * Preview receipt in a new window instead of downloading
 * @param {Object} appointment - The appointment object
 * @param {Object} clinicInfo - Clinic information
 */
export const previewAppointmentReceipt = (appointment, clinicInfo = {}) => {
  const doc = new jsPDF();

  // Use the same generation logic but open in new window
  // For brevity, we'll just call the main function and modify the output
  // In production, you'd want to refactor to share the generation logic

  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

